import { Timestamp } from "firebase/firestore";

import { CreateUserInput } from "src/contexts/AuthContext";
import { DocumentExtraction } from "src/features/documents/types/DocumentExtraction";
import { VocationId } from "src/features/quizzes/types/atom";
import { ResumeStatus } from "src/features/resume/types/ResumeStatus";
import { BackgroundFormData } from "src/pages/BackgroundSurveyScreen/BackgroundSurveyView";
import { StipendInformation } from "src/pages/DashboardPage/RangeTab/StipendSetupForm";
import { ProofOfIncomeType } from "src/shared/compliance/incomeDocuments";
import TransportationSituation from "src/shared/utils/enums/TransportationSituation";
import { EmployerId } from "src/types/Employer";
import { JobPreferenceFormData } from "src/types/JobPreference";
import {
  UserOnlineTrainingPlan,
  UserResourceProgress,
} from "src/types/StudyPlan";
import { BackgroundCheckReport } from "./BackgroundCheck";
import { CandidateProfile } from "./CandidateProfile";
import { OrganizationId, TrainingStage } from "./Organization";
import USState from "./states";
import {
  ApplicationStatus,
  DeprecatedApplicationStatus,
  EmploymentStatus,
  EnrollmentStatus,
  IncarcerationStatus,
} from "./UserStatus";

export type EnrollmentReadinessOverride = {
  overriddenAt: string;
  overriddenBy: string;
  overriddenByName: string;
  reason?: string;
};

export type PostalAddress = {
  street: string; // e.g. "123 Main Street"
  street2?: string; // e.g. "APT A" (optional)
  city: string; // e.g. "San Francisco"
  state: string; // e.g. "CA"
  zipcode: string; // e.g. "90401"
};

// Postal address with recipient name (used for eligibility data)
export type PostalAddressWithName = PostalAddress & {
  name: string; // e.g. "John Alfred Smith"
};

export enum UserOnboardingStatus {
  CREATED_ACCOUNT = "created_account",
  APPLIED = "applied",
  PRE_ADMISSION = "pre_admission",
  ENROLLED = "enrolled",
  COMPLETED_THEORY = "theory_completed",
  RANGE = "range",
  GRADUATED = "graduated",
  LICENSED = "licensed",
  EMPLOYED = "employed",
  DROPPED = "dropped",
  GLOBAL_EXIT = "global_exit",
  REJECTED = "rejected",
  UNSUCCESSFUL_EXIT = "unsuccessful_exit",
}

export type UserEligibilityData = {
  legalFirstName: string;
  legalLastName: string;
  legalMiddleName: string;
  ssn: string;
  zipcode: string;
  driverLicenseNumber: string;
  driverLicenseState: USState;
  driverLicenseIssueDate: string;
  driverLicenseExpirationDate: string;
  hasPreviousDriverLicense: boolean;
  previousDriverLicenseNumber?: string;
  previousDriverLicenseState?: USState;
  postalAddress: PostalAddressWithName;
};

// Confidential eligibility decision stored in confidential collection
export type ConfidentialEligibilityDecision = {
  score: number;
  qualifiers: string[];
  disqualifiers: Array<{
    type: "on_hold" | "rejected";
    /** DisqualifierCode. Absent on decisions written before 2026-10. */
    code?: string;
    reason: string;
    blogLink?: string;
  }>;
  status: keyof typeof ApplicationStatus;
  calculatedAt: string;
};

// Public eligibility decision stored in application
export type PublicEligibilityDecision = {
  publicFeedback: string;
  internalFeedback: string;
  disqualifiers: Array<{
    /** DisqualifierCode. Absent on decisions written before 2026-10. */
    code?: string;
    reason: string;
    blogLink?: string;
  }>;
  isEligible: boolean;
};

// TODO: nix this
export type UserTrainingData = {
  schoolId?: string;
  onboardingStatus: UserOnboardingStatus;
  // whether the user already has a DOL account
  dolId?: string;
  // the ID of the workforce board staff member assigned to the user
  careerNavigatorId?: string;
  // the ID of the primary Emerge admin assigned to the user. This remains the
  // coach used for communications and other single-coach workflows.
  emergeCoachId?: string;
  // Optional second coach. This is caseload-only: both coaches see the student
  // in Dial, while emergeCoachId remains the primary everywhere else.
  secondaryEmergeCoachId?: string;
  organizationId?: OrganizationId;
  cohortId?: string;
  /**
   * Server-owned resolution state for the asynchronous signup geo assignment.
   * Legacy users may not have this field; the onboarding gate retains a
   * time-based compatibility path for those records only.
   */
  organizationAssignmentStatus?: "pending" | "matched" | "no_match";
  // PR 5a — student-authored CDL test date (ISO date string), captured
  // via the chatbot's test_date step (`updateUserTestDate` op). Mirrors
  // `functions/types/User.ts:licenseTestDate`. Distinct from the
  // computed `UserSummary.licenseExamDate` which is read-only and
  // derived from this + other signals.
  licenseTestDate?: string | null;
};
export type UserSkillsTrainingDraftPlan = {
  skillsTrainingSchedule: string;
  projectedDurationWeeks: number;
};

// happens when users hit the driving yard
export type UserSkillsTrainingFullPlan = UserSkillsTrainingDraftPlan & {
  type: "full"; // for type narrowing
  transportationSituation: keyof typeof TransportationSituation;
} & StipendInformation;

export type UserSkillsTrainingPlan =
  | UserSkillsTrainingDraftPlan
  | UserSkillsTrainingFullPlan;

export type UserSkillsCoachChecklist = {
  hasIssuedTheoryCertification?: boolean;
  hasSubmittedSchoolEnrollment?: boolean;
  hasPaidSchool?: boolean;
  hasSubmittedFundingAgencyEnrollment?: boolean;
};

/** A student's response to one configured agency-involvement requirement. */
export type AgencyInvolvementResponse = {
  /** The stable ids of agencies the student says they are involved with. */
  selectedAgencyIds: string[];
  /** Set only after the student submits their answer, including "none". */
  submittedAt: string;
  updatedAt: string;
};

export const JOB_SEARCH_APPROACHES = [
  "self_directed",
  "mid_touch",
  "high_touch",
] as const;
export type JobSearchApproach = typeof JOB_SEARCH_APPROACHES[number];

export type UserAccount = {
  uid: string;
  middleName?: string;
  /** Student's phone number (E.164). Present on the doc; surfaced in the
   * Success Queue header and used as the SMS recipient. */
  phone?: string;
  /** Set only after the student proves phone possession during signup. */
  phoneVerifiedAt?: string;
  phoneVerifiedAtFirestoreTimestamp?: Timestamp;
  /**
   * Application-level SMS opt-out. `null`/absent means opted in; a Timestamp
   * means the student replied with a STOP-class keyword on a managed line.
   */
  smsOptOutAt?: Timestamp | null;
  /** Last authoritative START/STOP or provider opt-out update. */
  smsConsentUpdatedAt?: Timestamp | null;

  // NEW status fields
  incarcerationStatus?: keyof typeof IncarcerationStatus;
  incarcerationStatusDetails?: string;
  incarcerationStatusLastUpdatedAt?: string;
  applicationStatus?:
    | keyof typeof ApplicationStatus
    | keyof typeof DeprecatedApplicationStatus;
  applicationStatusDetails?: string;
  applicationStatusLastUpdatedAt?: string;
  /**
   * Snooze wake time for a MANUAL "On Hold" (mirrors the backend field). When
   * set, the hourly on-hold wake sweep auto-reverts the student to
   * `statusBeforeHold` at/after this time so the hold resurfaces. ABSENT = an
   * indefinite hold that never auto-wakes. Only set while
   * `applicationStatus === "on_hold"`.
   */
  holdWakeAt?: Timestamp;
  /**
   * The `applicationStatus` captured when a manual On Hold was applied, so the
   * wake sweep returns the student to exactly where they were. Snapshotted on
   * the way into on_hold, cleared on the way out.
   */
  statusBeforeHold?: keyof typeof ApplicationStatus;
  /**
   * Stamped when a snoozed hold auto-reverts, so the Enrollment Compliance dash
   * can flag "just came back from hold" for a window.
   */
  resurfacedFromHoldAt?: Timestamp;
  /**
   * Retry marker for returned_from_hold case side effects. Cleared once the
   * case marker lands; if case creation fails after wake, the scheduler retries
   * from this field.
   */
  returnedFromHoldCasePendingAt?: Timestamp;
  /** Original wake occurrence time used when retrying the returned_from_hold marker. */
  returnedFromHoldCaseOccurredAt?: Timestamp;
  enrollmentStatus?: keyof typeof EnrollmentStatus;
  enrollmentStatusDetails?: string;
  enrollmentStatusLastUpdatedAt?: string;
  /**
   * ISO timestamp of the moment an admin moved the user into Week 0 by
   * clicking "Move to Week 0" in EnrollmentReadinessCard. Single source of
   * truth for whether the admin has fired the "Started Week Zero" CIO event;
   * student interaction with the Week 0 study tool does NOT set this.
   */
  weekZeroStartedAt?: string;
  /**
   * Admin override that marks the student as 100% enrollment-ready, bypassing
   * the EnrollmentReadinessCard checklist and surfacing them on the
   * SchoolEnrollmentView dash even if individual gates (TPR submission,
   * background check, etc.) are still open. Set from the "Override readiness"
   * button on EnrollmentReadinessCard. Unset = no override.
   */
  enrollmentReadinessOverride?: EnrollmentReadinessOverride;
  employmentStatus?: keyof typeof EmploymentStatus;
  employmentStatusDetails?: string;
  employmentStatusLastUpdatedAt?: string;

  resources: { [resourceId: string]: UserResourceProgress };
  lastLoggedIn?: string;
  createdAt: string;
  appVersion?: string;
  // video ask profile
  videoAskRespondentId?: string;

  // background check
  backgroundCheckReport?: BackgroundCheckReport;

  // application form - no longer contains eligibility score
  application?: BackgroundFormData;

  // Journey job-match candidate profile. Server-written only: rules deny any
  // owner update touching this key, so never spread it back into a user write.
  // Mirror of `functions/types/User.ts` (hand-synced).
  jobMatchProfile?: CandidateProfile;

  // success plan onboarding
  jobPreference?: JobPreferenceFormData & {
    // NYCSBUS school bus interest experiment (Job Search Success Plan).
    // true = "Yes, send me info", false = "Not interested", absent = never asked.
    interestedInSchoolBusDriving?: boolean;
    createdAt?: string;
    createdAtFirestoreTimestamp?: Timestamp;
    // Set on coach edits from the Job placement intake card (createdAt is
    // when the student answered the wizard).
    updatedAt?: string;
    updatedAtFirestoreTimestamp?: Timestamp;
  };
  jobSearchPlan?: {
    approach: JobSearchApproach;
    createdAt?: string;
    createdAtFirestoreTimestamp?: Timestamp;
    updatedAt?: string;
    updatedAtFirestoreTimestamp?: Timestamp;
  };
  /**
   * Partner-employer placement (job-search stage). Optional FK to an employer
   * in the `employers` library the student was placed with; set by an admin on
   * the profile and surfaced as a job-search signal. A denormalized pointer
   * (not an embedded copy) so it never drifts from the employer record.
   */
  partnerEmployerPlacement?: {
    employerId: EmployerId;
    placedAt?: string;
  };
  /**
   * `dot-booking-gate-lesson-7` experiment stamp. Written by the gate UI when
   * a gate-test student enters the forced flow (`fundsVariant`, `enrolledAt`);
   * the backend adds `bookedAt` when the MEDICAL_EXAM milestone lands and
   * `fundsOrderId`/`fundsSentAt` once the Tremendous transit gift is placed —
   * or `fundsSkipped*` when the send was withheld (another account with the
   * same phone number already drew the once-per-phone gift).
   * Mirrors `functions/types/User.ts:dotBookingGateExperiment` (hand-synced).
   * See src/features/dot-booking-gate/experiment.ts.
   */
  dotBookingGateExperiment?: {
    fundsVariant: "control" | "test";
    enrolledAt: string;
    bookedAt?: string;
    fundsOrderId?: string;
    fundsSentAt?: string;
    fundsSkippedReason?: "duplicate_phone";
    fundsSkippedAt?: string;
    /** Gate treatment last experienced; absent = hard-wall era (pre 9/20). */
    gateMode?: "soft";
    /** Most recent soft exit (kept studying instead of booking). */
    lastSoftPassAt?: string;
  };
  /**
   * `permit-exam-gate-lesson-10` student report. `appointmentBlockedAt` is set
   * when a student outside NY says their DMV won't let them book; the gate
   * stops forcing them from then on. Nothing notifies a coach yet.
   * Mirrors `functions/types/User.ts:permitExamGate` (hand-synced).
   */
  permitExamGate?: {
    appointmentBlockedAt?: string;
    /**
     * ISO timestamp of the first tap on "Open {state} DMV scheduling". A
     * permanent gate release: the booking happens on a site we can't observe,
     * so the tap is the last signal the app gets, and holding a student who
     * tried behind a wall they can't clear costs them the whole course.
     */
    schedulerOpenedAt?: string;
  };
  theoryTrainingPlan?: UserOnlineTrainingPlan;

  skillsTrainingPlan?: UserSkillsTrainingPlan;

  skillsCoachChecklist?: UserSkillsCoachChecklist;

  /**
   * Responses are keyed by the stable requirement id. Keeping responses on the
   * user document lets the shared live-user listener update the checklist
   * immediately after a student submits it.
   */
  agencyInvolvementResponses?: Record<string, AgencyInvolvementResponse>;

  // orientation videos
  hasCompletedOrientationLesson?: boolean;

  // partnerId
  referredByPartnerId?: string;

  /**
   * Every partner this student is attributed to, `referredByPartnerId`
   * included. Multi-valued so one student can appear on several partners'
   * referral loads at once — e.g. the community org that enrolled them and the
   * city agency supervising them.
   *
   * `referredByPartnerId` remains the single PRIMARY referrer and stays the
   * only field that drives bonuses, Customer.io referral events and
   * support-network notifications, so a co-referrer never double-pays or
   * double-notifies. Dashboards and rosters read this array instead.
   *
   * Server-owned: the signup callable derives it from the resolved referral
   * code, and only backend code writes it. Mirrors
   * `functions/types/User.ts`.
   */
  referredByPartnerIds?: string[];

  /**
   * Server-verified evidence that the primary/default partner's referral
   * establishes justice involvement. This cannot be supplied by the signup
   * client; eligibility may trust it without inventing a specific criminal
   * history answer.
   */
  justiceInvolvementReferralPartnerId?: string;

  /**
   * The `users/{uid}` of the student whose referral code brought this user in.
   * Deliberately separate from `referredByPartnerId`: that field drives partner
   * bonus tracking and the Customer.io partner sync, and overloading it would
   * fire partner-side effects for student referrals.
   *
   * Written once at signup, but only by convention — like `referredByPartnerId`
   * it is a client write under the owner-writable `users/{userId}` rule, so an
   * account holder can currently rewrite it. Harden that (write-once in
   * `firestore.rules`, or move the write behind a callable) before any reward
   * pays out on it.
   */
  referredByUserId?: string;

  // Marketing attribution captured at signup. Nested per source so new
  // providers remain additive. Each block carries its own `capturedAt` since a
  // user can arrive through different sources at different times. All optional
  // — older docs won't have it.
  marketing?: {
    // Meta `_fbc`/`_fbp` cookies + `fbclid`. Sent RAW on the CAPI Lead event
    // for deterministic ad-click attribution.
    meta?: {
      fbc?: string;
      fbp?: string;
      fbclid?: string;
      capturedAt?: string;
    };
    // Google Ads click ids from the landing URL / `_gcl_aw` cookie. Sent RAW on
    // the Enhanced Conversions upload for deterministic ad-click attribution.
    // `wbraid`/`gbraid` are the iOS/privacy fallbacks Google uses when `gclid`
    // can't be set.
    google?: {
      gclid?: string;
      wbraid?: string;
      gbraid?: string;
      capturedAt?: string;
    };
    // Standard URL campaign fields. `campaign` identifies the event/campaign;
    // `content` can identify the exact post, creative, or link within it.
    utm?: {
      source?: string;
      medium?: string;
      campaign?: string;
      term?: string;
      content?: string;
      capturedAt?: string;
    };
  };

  // email alias for job applications tracking
  emergeCareerEmailAlias?: string;

  /**
   * @deprecated Exists for users who started a background check before April 7, 2026.
   * New writes use `postalAddress`, `driverLicense`, and `middleName` on the user profile.
   * SSN is no longer stored. Use `getPostalZipcode()` and `getDriverLicense()` from
   * `src/shared/utils/userFieldHelpers` for reads with automatic fallback.
   */
  eligibilityData?: UserEligibilityData;

  // Driver's license postal address (for vendor document signing)
  postalAddress?: PostalAddress;

  driverLicense?: {
    number: string;
    state: USState;
  };

  /**
   * Commercial credential the student currently holds — "permit" (CLP) or
   * "license" (CDL); a license supersedes a permit. Captured by an admin at
   * permit/license verification. Vocation-agnostic.
   */
  credentialLevel?: "permit" | "license";
  /**
   * Endorsement codes held on the student's permit/license (e.g. "H", "S").
   * Captured at verification; surfaced as a job-search signal. See
   * `src/shared/compliance/endorsements.ts` for the CDL option list.
   */
  endorsements?: string[];

  /**
   * The vocation the student is training/job-searching for. Defaults to
   * "cdl" when undefined (the only vocation today) — matching treats an
   * unset value as "cdl" so no student is silently excluded. Mirrors
   * `functions/types/User.ts` (hand-synced).
   */
  vocationId?: VocationId;

  // Resume builder
  resume?: {
    status?: ResumeStatus;
    pdfUrl?: string;
    /** Raw PDF the student uploaded. */
    uploadedPdfStoragePath?: string;
    /** Polished PDF produced by the generation pipeline. */
    generatedPdfStoragePath?: string;
    lastUpdatedAt?: Timestamp;
    vocationType?: string;
  };

  // only exists when the user is on skills training
  latestAttendanceDate?: string;
  latestAttendanceDateFirestoreTimestamp?: Timestamp;

  /**
   * IANA timezone (e.g., `America/New_York`). Optional — the Cal.com slot
   * picker falls back to the browser's resolved timezone, then to EST.
   * No code path requires this to be present.
   */
  timeZone?: string;

  // Compliance wizard — captured in Step 1 (target start date).
  // `targetStartDate` is null when the student picked "I'm not sure yet";
  // the bucket field is the segmentable label in either case.
  targetStartDate?: Timestamp | null;
  targetStartDateBucket?: TargetStartDateBucket;
} & UserTrainingData &
  CreateUserInput;

export type {
  UserResourceProgress,
  UserStudyGuideProgress,
  UserVideoProgress,
} from "src/types/StudyPlan";

export const TARGET_START_DATE_BUCKETS = [
  "asap",
  "2_4_weeks",
  "1_2_months",
  "unsure",
] as const;
export type TargetStartDateBucket = typeof TARGET_START_DATE_BUCKETS[number];

/** Shared fields present on every uploaded document. */
type BaseUploadedDocument = {
  uid: string;
  path: string;
  userId: string;
  stage?: TrainingStage;
  verifiedAt?: string | null;
  verifiedAtFirestoreTimestamp?: Timestamp | null;
  verifiedById?: string | null;
  verifiedByName?: string | null;
  rejectedAt?: string | null;
  rejectedAtFirestoreTimestamp?: Timestamp | null;
  rejectedById?: string | null;
  rejectedByName?: string | null;
  rejectionReason?: string | null;
  organizationDocumentId?: string; // Only if the source is an organization, note the edge case in case the person gets moved from one organization to another
  // These fields were only included on the Feb 10, 2025 release
  createdAt?: string;
  createdAtFirestoreTimestamp?: Timestamp;
  /**
   * Additive Document AI / Gemini extraction result, written by the extraction
   * worker onto this document. Flows through `fetchDocuments` untouched (the
   * fetch/normalize/guard pipeline preserves unknown fields). Optional: only
   * present once extraction has run for this document.
   */
  extraction?: DocumentExtraction;
};

/** proof_of_income documents must specify which sub-type was uploaded. */
type ProofOfIncomeDocument = BaseUploadedDocument & {
  name: "proof_of_income";
  proofType: ProofOfIncomeType;
};

/** All other documents have no proofType. */
type StandardDocument = BaseUploadedDocument & {
  name: string;
  proofType?: never;
  /** Present only on documents uploaded for agency-involvement requirements. */
  agencyInvolvement?: {
    requirementId: string;
    agencyId: string;
    agencyName: string;
  };
};

export type UserUploadedDocument = ProofOfIncomeDocument | StandardDocument;

export enum UserType {
  User = "user",
  Admin = "admin",
  Partner = "partner",
}
